import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import SEOHead from '../SEOHead';
import { Card } from '../ui';
import { acme } from '../../theme/acmeTheme';
import {
  legalPageOuterSx,
  legalPageCardSx,
  legalPageTitleSx,
  legalPageUpdatedSx,
  legalSectionTitleSx,
  legalBodySx,
  legalFooterSx,
} from '../legal/legalDocumentStyles';

const LEGAL_LAST_UPDATED = 'June 19, 2026';

const TermsOfService = () => {
  return (
    <>
      <SEOHead pageName="terms-of-service" />
      <Box sx={legalPageOuterSx}>
      <Card elevation={0} sx={legalPageCardSx}>
        <Typography component="h1" sx={legalPageTitleSx}>
          Terms of Service
        </Typography>
        <Typography sx={legalPageUpdatedSx}>
          Last updated: {LEGAL_LAST_UPDATED}
        </Typography>
        <Divider sx={{ my: 3, borderColor: acme.colors.border }} />

        <Box my={4}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            1. Company Information and Service Provider
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>1.1 Service Provider:</strong> Mailsball is a software-as-a-service (SaaS) email and SMS marketing platform (the "Service") provided by <strong>Atikut OÜ</strong>, a private limited company (Estonian <em>osaühing</em>, registry code <strong>17460898</strong>, registered on 17 March 2026) ("Company", "we", "our", "us").
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>1.2 Operator:</strong> The Mailsball platform is owned and operated by Atikut OÜ. Oleksandr Khrustalov serves on the management board of Atikut OÜ and is the sole shareholder and ultimate beneficial owner.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>1.3 Service Definition:</strong> These Terms of Service ("Terms", "ToS") govern your use of our comprehensive email and SMS marketing platform accessible at mailsball.com, including all related services, features, applications, and functionalities.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>1.4 Agreement:</strong> By accessing, using, or registering for the Service, you agree to be bound by these Terms in their entirety. If you disagree with any part of these Terms, you do not have permission to access or use the Service.
          </Typography>
        </Box>

        <Box my={4}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            2. Service Description and Scope
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>2.1 Platform Features:</strong> Mailsball provides a comprehensive email and SMS marketing solution including but not limited to: email and SMS campaign creation and management, contact and phone list management, automated sequences, analytics and reporting, template and message design tools, deliverability tools, SMS sender label/phone registration, SMS credit wallet and billing, opt-out handling, and integration capabilities.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>2.2 Service Availability:</strong> We strive to maintain 99.9% uptime but do not guarantee uninterrupted service. Email and SMS delivery depend on third-party networks, carriers, and mailbox providers. We reserve the right to perform maintenance, updates, or modifications to the Service at our discretion.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>2.3 Account Registration:</strong> To use the Service, you must register for an account by providing accurate, complete, and current information. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>2.4 SMS Services:</strong> SMS functionality is provided through third-party telecommunications aggregators and mobile network operators. Availability, routing, throughput, sender registration requirements, and pricing vary by destination country. We may modify SMS features, rates, supported destinations, or providers at any time with reasonable notice where practicable.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>2.5 Technical Intermediary:</strong> We provide the Service as a technical platform only. We do not review, endorse, or assume responsibility for the content, legality, or recipient lists you upload or messages you send through the Service, except where required by mandatory law.
          </Typography>
        </Box>

        <Box my={4}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            3. User Responsibilities and Acceptable Use
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>3.1 Account Security:</strong> You are solely responsible for maintaining the security of your account credentials, including usernames, passwords, and API keys. You must immediately notify us of any unauthorized access or security breaches.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>3.2 Content Responsibility:</strong> You retain full ownership and responsibility for all content you upload, create, or distribute through our Service, including but not limited to email campaigns, SMS messages, contact lists, phone lists, images, and other materials.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>3.3 Compliance Obligations:</strong> You agree to comply with all applicable laws and regulations, including but not limited to:
          </Typography>
          <Typography component="ul" sx={{ pl: 4 }}>
            <li><Typography paragraph>General Data Protection Regulation (GDPR), ePrivacy Directive, and other EU privacy and electronic communications laws</Typography></li>
            <li><Typography paragraph>CAN-SPAM Act and other anti-spam legislation</Typography></li>
            <li><Typography paragraph>Canada's Anti-Spam Legislation (CASL)</Typography></li>
            <li><Typography paragraph>California Consumer Privacy Act (CCPA) and other US state privacy laws</Typography></li>
            <li><Typography paragraph>US TCPA, CTIA messaging principles, and other SMS/telemarketing rules where applicable</Typography></li>
            <li><Typography paragraph>Estonian Data Protection Act, Estonian Electronic Communications Act, and other local regulations</Typography></li>
            <li><Typography paragraph>Carrier, operator, and SMS aggregator acceptable use policies</Typography></li>
            <li><Typography paragraph>Any other applicable data protection, privacy, or electronic communications laws</Typography></li>
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>3.4 SMS-Specific Obligations:</strong> For SMS campaigns you warrant that: (a) every recipient has provided valid prior express consent or another lawful basis to receive promotional SMS from you; (b) you maintain records of consent; (c) you use only authorized sender labels/numbers registered on your account; (d) you include required opt-out mechanisms and honour STOP/unsubscribe requests without undue delay; (e) you do not send SMS to numbers on do-not-call or suppression lists where applicable; and (f) your message content complies with local telecommunications and marketing rules in each destination country.
          </Typography>
        </Box>

        <Box my={4}>
          <Typography component="h2" sx={{ ...legalSectionTitleSx, color: 'error.main' }}>
            4. Strictly Prohibited Activities
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>4.1 Prohibited Conduct:</strong> You explicitly agree NOT to:
          </Typography>
          <Typography component="ul" sx={{ pl: 4 }}>
            <li><Typography paragraph>Send unsolicited emails, SMS, spam, or communications to recipients without valid consent or another lawful basis</Typography></li>
            <li><Typography paragraph>Send SMS without functional opt-out instructions or fail to process STOP/unsubscribe requests</Typography></li>
            <li><Typography paragraph>Use purchased, scraped, rented, or third-party phone lists without documented consent</Typography></li>
            <li><Typography paragraph>Spoof sender identity, use unauthorized sender labels, or misrepresent the origin of messages</Typography></li>
            <li><Typography paragraph>Engage in any fraudulent, deceptive, or misleading practices including phishing, spoofing, or impersonation</Typography></li>
            <li><Typography paragraph>Distribute malware, viruses, or any malicious code through the Service</Typography></li>
            <li><Typography paragraph>Harvest, scrape, or collect email addresses or phone numbers without proper consent</Typography></li>
            <li><Typography paragraph>Use the Service for illegal purposes or to distribute illegal content</Typography></li>
            <li><Typography paragraph>Attempt to gain unauthorized access to our systems or other users' accounts</Typography></li>
            <li><Typography paragraph>Reverse engineer, decompile, or attempt to extract source code from our Service</Typography></li>
            <li><Typography paragraph>Resell, redistribute, or sublicense the Service without written permission</Typography></li>
            <li><Typography paragraph>Use the Service to send content that violates third-party intellectual property rights</Typography></li>
            <li><Typography paragraph>Circumvent or disable security features, rate limiting, or access controls</Typography></li>
          </Typography>
          
          <Typography paragraph sx={{ fontWeight: 'bold' }}>
            <strong>4.2 Content Restrictions:</strong> You may not use the Service to distribute content that is:
          </Typography>
          <Typography component="ul" sx={{ pl: 4 }}>
            <li><Typography paragraph>Illegal, harmful, threatening, abusive, or harassing</Typography></li>
            <li><Typography paragraph>Defamatory, vulgar, obscene, or invasive of privacy</Typography></li>
            <li><Typography paragraph>Discriminatory based on race, religion, gender, sexual orientation, or other protected characteristics</Typography></li>
            <li><Typography paragraph>Related to illegal substances, weapons, or other prohibited items</Typography></li>
            <li><Typography paragraph>Promoting violence, terrorism, or other illegal activities</Typography></li>
          </Typography>
        </Box>

        <Box my={4}>
          <Typography component="h2" sx={{ ...legalSectionTitleSx, color: 'error.main' }}>
            5. Account Suspension and Termination
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>5.1 Immediate Termination:</strong> We reserve the right to immediately suspend or terminate your account and access to the Service, without prior notice or refund, if we determine that you have:
          </Typography>
          <Typography component="ul" sx={{ pl: 4 }}>
            <li><Typography paragraph>Violated any provision of these Terms</Typography></li>
            <li><Typography paragraph>Engaged in prohibited activities as defined in Section 4</Typography></li>
            <li><Typography paragraph>Provided false or misleading information during registration</Typography></li>
            <li><Typography paragraph>Attempted to circumvent our systems or security measures</Typography></li>
            <li><Typography paragraph>Caused damage to our reputation or business</Typography></li>
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>5.2 Consequences of Termination:</strong> Upon termination, you will lose access to all Service features, and we may delete your account data. We are not obligated to provide refunds for terminated accounts.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>5.3 Voluntary Termination:</strong> You may terminate your account at any time through your account settings. Termination does not relieve you of any obligations incurred prior to termination.
          </Typography>
        </Box>

        <Box my={4}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            6. Intellectual Property Rights
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>6.1 Our Intellectual Property:</strong> The Service, including all software, code, designs, text, graphics, interfaces, and other materials, is owned by Atikut OÜ and protected by intellectual property laws. You are granted a limited, non-exclusive, non-transferable license to use the Service solely for its intended purpose.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>6.2 Your Content:</strong> You retain ownership of all content you upload to the Service. By using the Service, you grant us a worldwide, royalty-free license to use, store, and process your content solely for the purpose of providing the Service.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>6.3 Feedback:</strong> Any feedback, suggestions, or ideas you provide regarding the Service may be used by us without compensation or attribution.
          </Typography>
        </Box>

        <Box my={4}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            7. Payment Terms and Billing
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>7.1 Subscription Fees:</strong> Use of the Service requires payment of subscription fees as outlined in your chosen plan. All fees are charged in advance and are non-refundable unless otherwise specified.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>7.2 Payment Processing:</strong> Payments are processed by third-party payment processors. We do not store your complete payment information on our servers.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>7.3 Billing Disputes:</strong> Any billing disputes must be reported within 30 days of the charge. We will investigate and resolve disputes in good faith.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>7.4 Automatic Renewal:</strong> Subscriptions automatically renew unless cancelled before the renewal date. You may cancel your subscription at any time through your account settings.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>7.5 SMS Credits and Prepaid Wallet:</strong> SMS sending is billed through prepaid SMS credits purchased via the platform unless your plan includes unlimited SMS. Credit prices vary by destination country and may change. Credits are consumed per successfully charged message segment as reported by our billing systems. Except where mandatory law requires otherwise, SMS credit purchases are non-refundable and non-transferable. Unused credits may expire or be forfeited upon account termination at our discretion, subject to applicable consumer protection law.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>7.6 Taxes:</strong> Prices may exclude VAT or other taxes unless stated otherwise. You are responsible for applicable taxes associated with your purchases.
          </Typography>
        </Box>

        <Box my={4}>
          <Typography component="h2" sx={{ ...legalSectionTitleSx, color: 'error.main' }}>
            8. Disclaimers and Limitation of Liability
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>8.1 Service Disclaimer:</strong> THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT EMAIL OR SMS MESSAGES WILL BE DELIVERED, DELIVERED WITHIN A SPECIFIC TIME, OR ACCEPTED BY CARRIERS OR RECIPIENTS.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>8.2 SMS Deliverability:</strong> SMS delivery is subject to carrier filtering, regulatory restrictions, handset settings, roaming, and third-party provider capacity. We are not liable for message delays, failures, blocking, throttling, or inaccurate delivery reports provided by upstream networks.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>8.3 Limitation of Liability:</strong> IN NO EVENT SHALL ATIKUT OÜ OR ITS EMPLOYEES, DIRECTORS, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR USE, ARISING OUT OF OR RELATING TO THE SERVICE.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>8.4 Maximum Liability:</strong> Our total liability for any claim arising out of or relating to the Service shall not exceed the amount you paid for the Service in the 12 months preceding the claim.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>8.5 Indemnification:</strong> You agree to indemnify, defend, and hold harmless Atikut OÜ and its respective employees, directors, and agents from any claims, damages, losses, fines, penalties, costs, and expenses (including reasonable attorneys' fees) arising from: (a) your use of the Service; (b) your email or SMS content, recipient lists, or lack of consent; (c) your violation of these Terms or applicable law; (d) claims by recipients, carriers, regulators, or third parties relating to messages sent through your account; or (e) your infringement of third-party rights.
          </Typography>
        </Box>

        <Box my={4}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            9. Data Protection and Privacy
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>9.1 Privacy Policy:</strong> Our collection, use, and protection of your personal data is governed by our Privacy Policy, which is incorporated into these Terms by reference.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>9.2 Data Controller:</strong> For the purposes of data protection law, Atikut OÜ acts as the data controller for your account information and service usage data.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>9.3 Data Processing:</strong> You act as the data controller for any personal data you process through the Service (such as your email and SMS recipients), and we act as the data processor for such data, processing it only on your instructions to provide the Service. Our Privacy Policy describes how we handle personal data and forms part of these Terms.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>9.4 Data Processing Agreement:</strong> By using the Service to process personal data of third parties, you instruct us to process that data in accordance with these Terms and our Privacy Policy. You are responsible for entering into any additional data processing agreement required by your organization or applicable law.
          </Typography>
        </Box>

        <Box my={4}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            10. Force Majeure
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            We shall not be liable for any failure or delay in performance of our obligations under these Terms if such failure or delay is due to circumstances beyond our reasonable control, including but not limited to acts of God, war, terrorism, pandemic, government actions, or technical failures of third-party services.
          </Typography>
        </Box>

        <Box my={4}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            11. Export Control and International Use
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>11.1 Export Compliance:</strong> You agree to comply with all applicable export control laws and regulations. You may not use the Service if you are located in a country subject to embargo or if you are on any government restricted parties list.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>11.2 International Use:</strong> If you access the Service from outside Estonia, you are responsible for compliance with local laws and regulations.
          </Typography>
        </Box>

        <Box my={4}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            12. Modifications to Terms
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>12.1 Right to Modify:</strong> We reserve the right to modify these Terms at any time. Material changes will be communicated via email or prominent notice on our website at least 30 days before taking effect.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>12.2 Acceptance of Changes:</strong> Continued use of the Service after changes take effect constitutes acceptance of the modified Terms. If you do not agree with the changes, you must discontinue use of the Service.
          </Typography>
        </Box>

        <Box my={4}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            13. Governing Law and Dispute Resolution
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>13.1 Governing Law:</strong> These Terms shall be governed by and construed in accordance with the laws of the Republic of Estonia, without regard to its conflict of law provisions.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>13.2 Jurisdiction:</strong> Any disputes arising out of or relating to these Terms or the Service shall be subject to the exclusive jurisdiction of the courts of Estonia.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>13.3 Dispute Resolution:</strong> Before initiating legal proceedings, parties agree to attempt to resolve disputes through good faith negotiations. If unsuccessful, disputes may be submitted to binding arbitration under Estonian law.
          </Typography>
        </Box>

        <Box my={4}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            14. Miscellaneous
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>14.1 Entire Agreement:</strong> These Terms constitute the entire agreement between you and us regarding the Service and supersede all prior communications and proposals.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>14.2 Severability:</strong> If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>14.3 Waiver:</strong> Our failure to enforce any provision of these Terms shall not constitute a waiver of that provision or any other provision.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>14.4 Assignment:</strong> You may not assign or transfer these Terms or your rights hereunder without our written consent. We may assign these Terms without restriction.
          </Typography>
        </Box>

        <Box my={4}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            15. SMS Marketing Supplemental Terms
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>15.1 Third-Party Providers:</strong> SMS transmission may involve sub-processors and telecommunications providers (including BulkGate and underlying mobile operators). Their technical requirements, filtering rules, and registration procedures apply in addition to these Terms.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>15.2 Sender Registration:</strong> You must complete any sender label, company, or number verification required before sending. We may suspend SMS sending until verification is complete or if provider approval is revoked.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>15.3 Opt-Out Mechanisms:</strong> The platform may automatically append opt-out instructions or links to SMS messages. You remain responsible for ensuring compliance with all applicable opt-out rules and for not re-contacting opted-out recipients except as permitted by law.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>15.4 Monitoring and Enforcement:</strong> We may review SMS traffic patterns, complaint rates, and provider alerts. We may throttle, queue, block, or terminate SMS access without refund if we reasonably suspect abuse, unlawful content, or carrier policy violations.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>15.5 Regulatory Cooperation:</strong> You authorize us to disclose account and traffic information to carriers, aggregators, or authorities when required to investigate abuse, comply with law, or respond to lawful requests.
          </Typography>
        </Box>

        <Box my={4}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            16. Contact Information
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>Service provider:</strong> Atikut OÜ (Osaühing)<br />
            <strong>Registry code:</strong> 17460898 (Estonian Commercial Register)<br />
            <strong>Address:</strong> Estonia<br />
            <strong>Email:</strong> support@mailsball.com<br />
            <strong>Phone:</strong> +372 5359 1740
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>Contact person (management board):</strong> Oleksandr Khrustalov<br />
            <strong>Email:</strong> aleksandr@mailsball.com
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            For legal matters and compliance inquiries, please contact our legal team at legal@mailsball.com.
          </Typography>
        </Box>

        <Divider sx={{ mt: 5, mb: 3, borderColor: acme.colors.border }} />
        <Typography sx={legalFooterSx}>
          © {new Date().getFullYear()} Atikut OÜ. All rights reserved.
        </Typography>
      </Card>
    </Box>
    </>
  );
};

export default TermsOfService; 